import { Box, Container, useTheme } from "@material-ui/core";
import { graphql } from "gatsby";
import get from "lodash/get";
import React from "react";
import fastCompare from "react-fast-compare";
import useIsAuthenticated from "../hooks/use-is-authenticated";

import BatteryPriceSection from "./battery-price";
// import BrandsListSection from "./brands-list-section";
import Card from "./card";
import CardGrid from "./card-grid";
import DeviceFeaturesBarChartsSection from "./device-features-bar-chart";
import ExpressDeliveryInfoSection from "./express-delivery-info";
import FaqEmbeddableSection from "./faq-embeddable-section";

// Airgraft 2
import ProductsListSection from "./products-list-section";
import ShopNavigation from "./shop-navigation";
import ContentGuide from "./content-guide";
import ContentHero from "./content-hero";
// import ContentList from "./content-list";

// Re-usable content modules
import Spacer from "./spacer";
import ButtonSection from "./button-section";
import BackButtonSection from "./back-button-section";
import SupportSection from "./support-section";
import TitleSection from "./title-section";
// import StoryVideoButton from "./story-video-button";
// import AlertMessageSection from "./alert-message";
import JoinClubSection from "./join-club-section";
import FreeDeliveryBanner from "./free-shipping-banner";
import { useContextSelector } from "use-context-selector";
import { SubcountryContext } from "../providers/subcountry";
import { getDefaultSubcountry } from "../helpers/country";
import useCountry from "../hooks/use-country";
import NewsletterSection2 from "./newsletter-section2";
import PartnersFlipcard from "./partners-flipcard";
import PartnersQuote from "./partners-quote";
import PartnersRequestSampleKit from "./partners-request-sample-kit";
import PartnersDeepTechDive from "./partners-deep-tech-dive";
import MedicalFormSection from "./medical-form-section";
import MedicalCardsSection from "./medical-cards";

const contentModuleComponents = {
  c_module_faq_section: FaqEmbeddableSection,
  c_module_card: Card,
  c_module_card_grid: CardGrid,
  c_module_spacer: Spacer,
  c_module_back_button: BackButtonSection,
  c_module_title_section: TitleSection,
  c_module_button_section: ButtonSection,
  c_module_newsletter_section2: NewsletterSection2,
  c_module_device_features_section: DeviceFeaturesBarChartsSection,
  c_module_battery_price: BatteryPriceSection,
  c_module_support_section: SupportSection,
  c_module_products_list_section: ProductsListSection,
  c_module_shop_navigation: ShopNavigation,
  // c_module_brands_list_section: BrandsListSection,
  c_module_express_delivery_info: ExpressDeliveryInfoSection,
  c_module_content_guide: ContentGuide,
  c_module_content_hero: ContentHero,
  // c_module_content_list: ContentList,
  // c_module_story_video_button: StoryVideoButton,
  // c_module_alert_message: AlertMessageSection,
  c_module_join_the_club: JoinClubSection,
  c_module_shop_free_delivery_section: FreeDeliveryBanner,
  c_module_partners_flipcard: PartnersFlipcard,
  c_module_partners_quote: PartnersQuote,
  c_module_partners_request_sample_kit: PartnersRequestSampleKit,
  c_module_partners_tech_deep_dive: PartnersDeepTechDive,
  c_module_medical_form_section: MedicalFormSection,
  c_module_medical_cards_section: MedicalCardsSection
};

export type ContentModuleType = {
  model?: { apiKey: string };
  visibilitySettings?: ReadonlyArray<Queries.ContentModuleVisibilitySettingFragmentFragment>;
};

export type ContainerMaxWidth = "fluid" | "xs" | "sm" | "md" | "lg" | "xl";

type Props = {
  contentModules: ReadonlyArray<ContentModuleType>;
  pageContainer?: ContainerMaxWidth;
  pageContainerDisableGutters?: boolean;
};

function ContentModules({
  contentModules,
  pageContainer,
  pageContainerDisableGutters
}: Props) {
  const theme = useTheme();
  const isAuthenticated = useIsAuthenticated();
  const country = useCountry();
  const selectedSubcountry = useContextSelector(
    SubcountryContext,
    s => s.subcountry
  );
  const subcountry = selectedSubcountry || getDefaultSubcountry(country);

  // Detect if any content modules has visibility settings
  // If the page has no content modules with visibility settings, we will not have to two-pass render the page.
  const hasAnyVisibilitySettings =
    contentModules.filter(
      contentModule =>
        (contentModule.visibilitySettings || []).filter(
          v =>
            (v.authentication && v.authentication !== "any") ||
            v.subcountries?.length > 0
        ).length > 0
    ).length > 0;

  // Two-pass rendering to hide/show modules after first render based on Content visibility settings
  // Only for pages with content modules visiblity settings.
  const [hasMounted, setHasMounted] = React.useState(false);
  React.useEffect(() => {
    if (hasAnyVisibilitySettings) {
      setHasMounted(true);
    }
  }, []);

  if (!contentModules || contentModules.length === 0) {
    return null;
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      flex="1 0 auto"
      className="page-content-modules"
    >
      {contentModules.map((contentModule, index) => {
        if (contentModule) {
          const { model, ...contentModuleProps } = contentModule;

          /**
           * Content module visibility settings
           */
          const visibilitySettings =
            contentModule?.visibilitySettings &&
            contentModule.visibilitySettings.length > 0
              ? contentModule.visibilitySettings[0]
              : null;

          // Dont render conditional module if the page has not rendered once.
          if (
            visibilitySettings?.authentication &&
            visibilitySettings?.authentication !== "any" &&
            !hasMounted
          ) {
            return null;
          }

          if (
            visibilitySettings?.authentication === "authenticated" &&
            !isAuthenticated // Dont render authenticated only content if the page has not mounted yet. Wait for two-pass rendering.
          ) {
            return null;
          }
          if (
            visibilitySettings?.authentication === "unauthenticated" &&
            isAuthenticated
          ) {
            return null; // Dont render unauthenticated only content if the page has not mounted yet. Wait for two-pass rendering.
          }

          if (
            visibilitySettings?.subcountries?.length > 0 &&
            !visibilitySettings?.subcountries.find(s => s.slug === subcountry)
          ) {
            return null; // Dont render if content should not be visible for current subcountry (State/Province)
          }

          /**
           * Content module container settings
           */
          // contentModule.containerWidth or fallback to webPage.containerWidth
          const containerMaxWidth =
            ((contentModule as Record<string, string>)
              .container as ContainerMaxWidth) ||
            pageContainer ||
            null;

          // contentModule.containerDisableGutters or fallback to webPage.containerDisableGutters
          const containerDisableGutters =
            ((contentModule as any).containerDisableGutters as boolean) ||
            pageContainerDisableGutters ||
            false;

          const containerStyles: React.CSSProperties = {};

          // contentModule.backgroundColor
          // Can be hex code or palette key: "primary.main"
          if ((contentModule as any).containerBackgroundColor) {
            const value = (contentModule as any)
              .containerBackgroundColor as string;
            containerStyles.backgroundColor = value.startsWith("#")
              ? value
              : get(theme.palette, value);
          }

          let innerContainerMaxWidth: ContainerMaxWidth = null;
          if ((contentModule as any).innerContainer) {
            innerContainerMaxWidth = (contentModule as any)
              .innerContainer as ContainerMaxWidth;
          }
          const innerContainerDisableGutters: boolean =
            (contentModule as any).innerContainerDisableGutters || false;

          if (model && model.apiKey) {
            const element = contentModuleComponents[model.apiKey];
            if (element) {
              return (
                <Container
                  key={`${model.apiKey}-${index}`}
                  maxWidth={
                    containerMaxWidth === "fluid" ? false : containerMaxWidth
                  }
                  disableGutters={containerDisableGutters}
                  data-content-module={model.apiKey}
                  style={containerStyles}
                >
                  <ConditionalContainer
                    maxWidth={
                      innerContainerMaxWidth === "fluid"
                        ? false
                        : innerContainerMaxWidth
                    }
                    disableGutters={innerContainerDisableGutters}
                  >
                    {React.createElement(element, {
                      ...contentModuleProps,
                      index
                    })}
                  </ConditionalContainer>
                </Container>
              );
            }
          }
        }

        return null;
      })}
    </Box>
  );
}

export default React.memo(ContentModules, fastCompare);

function ConditionalContainer({
  maxWidth,
  children,
  disableGutters
}: {
  maxWidth: ContainerMaxWidth | false;
  children: JSX.Element;
  disableGutters?: boolean;
}) {
  return maxWidth ? (
    <Container
      maxWidth={maxWidth as "xs" | "sm" | "md" | "lg" | "xl"}
      disableGutters={disableGutters}
    >
      {children}
    </Container>
  ) : (
    children
  );
}

export const ContentModuleVisibilitySettingFragment = graphql`
  fragment ContentModuleVisibilitySettingFragment on DatoCmsContentModuleVisibilitySetting {
    authentication
    subcountries {
      slug
      name
    }
  }
`;

export const WebPageContentModulesFragment = graphql`
  fragment WebPageContentModulesFragment on DatoCmsWebPage {
    modules {
      ...SpacerFragment
      ...TitleSectionFragment
      ...ButtonSectionFragment
      ...FaqEmbeddableSectionFragment
      ...CardFragment
      ...CardGridFragment
      ...NewsletterSection2Fragment
      ...DeviceFeaturesBarChartsFragment
      ...BatteryPriceFragment
      ...SupportSectionFragment
      ...ProductsListSectionFragment
      ...ShopNavigationFragment
      ...BrandsListSectionFragment
      ...FreeDeliveryBannerSectionFragment
      ...ExpressDeliveryInfoSectionFragment
      ...BackButtonFragment
      ...ContentGuideFragment
      ...ContentHeroFragment
      ...ContentListFragment
      ...AlertMessageFragment
      ...JoinClubFragment
      ...PartnersFlipcardFragment
      ...PartnersQuoteFragment
      ...PartnersRequestSampleKitFragment
      ...PartnersDeepTechDiveFragment
      ...DatoCmsCModuleMedicalFormSectionFragment
      ...DatoCmsMedicalCardsFragment
    }
  }
`;
